import gamesData from './catalog/games.json'
import studiosData from './catalog/studios.json'
import brandsData from './catalog/brands.json'
import streamsData from './catalog/streams.json'

export interface CatalogGame {
  code: string
  name: string
  devices?: Record<string, string>
}

export interface CatalogStudio {
  code: string
  name: string
  lat: number
  lon: number
  country: string
}

export interface CatalogBrand {
  code: string
  name: string
}

export interface CatalogStream {
  streamKey: string
  studioCode: string
  gameCode: string
  brand?: string[] | null
  tableType?: string | null
  deviceQuality?: string | null
  url?: string | null
  type?: string | null
}

/**
 * Static catalogs mirrored from `services/aggregator/src/data/catalog` (source of truth).
 * Swap these imports for `GET /api/catalog/{games,studios,brands,streams}` when a live
 * catalog exists — same boundary the aggregator's CatalogProvider targets.
 */
export const CATALOG_GAMES: CatalogGame[] = gamesData as CatalogGame[]
export const CATALOG_STUDIOS: CatalogStudio[] = studiosData as CatalogStudio[]
export const CATALOG_BRANDS: CatalogBrand[] = brandsData as CatalogBrand[]
export const CATALOG_STREAMS: CatalogStream[] = streamsData as CatalogStream[]

/** Live label / alias → catalog studio code. */
const STUDIO_ALIASES: Record<string, string> = {
  portomaso: 'PTM5',
  'portomaso level 5': 'PTM5',
  'ptm level 5': 'PTM5',
  'ptm5': 'PTM5',
  mriehel: 'MRH',
  'malta mriehel': 'MRH',
  mrh: 'MRH',
  'mrh-l1': 'MRH-L1',
  'mriehel level 1': 'MRH-L1',
  religa: 'RLG',
  'portomaso religa': 'PTM-RLG',
  'ptm-rlg': 'PTM-RLG',
  pt: 'PT',
  rlg: 'RLG',
  taiwan: 'TWN',
  taipei: 'TWN',
  tw: 'TW',
  twn: 'TWN',
}

function normStudioKey(raw: string): string {
  return raw.trim().toLowerCase().replace(/[_/]+/g, ' ').replace(/\s+/g, ' ')
}

/** Resolve a live studio code/label to a catalog entry (exact code, alias, or name). */
export function resolveCatalogStudio(raw: string): CatalogStudio | null {
  const key = String(raw ?? '').trim()
  if (!key) return null
  const byCode = CATALOG_STUDIOS.find((s) => s.code.toUpperCase() === key.toUpperCase())
  if (byCode) return byCode
  const alias = STUDIO_ALIASES[normStudioKey(key)]
  if (alias) {
    const hit = CATALOG_STUDIOS.find((s) => s.code === alias)
    if (hit) return hit
  }
  const byName = CATALOG_STUDIOS.find((s) => normStudioKey(s.name) === normStudioKey(key)
    || normStudioKey(s.name).includes(normStudioKey(key))
    || normStudioKey(key).includes(normStudioKey(s.name)))
  return byName ?? null
}
/** Stable demo-site pick for uncatalogued studios (never mid-Atlantic). */
export function demoSiteForStudio(seed: string): CatalogStudio {
  const sites = CATALOG_STUDIOS.filter((s) => Number.isFinite(s.lat) && Number.isFinite(s.lon))
  if (!sites.length) {
    return { code: 'PTM5', name: 'Portomaso', lat: 35.9236, lon: 14.4906, country: 'MT' }
  }
  let h = 2166136261
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return sites[(h >>> 0) % sites.length]!
}
